import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import {
  scheduleAppointmentStartNotification,
  checkAndSendPendingNotifications,
} from "../utils/notifications";
import { useNotifications } from "../context/NotificationContext";
import FloatingActionButton from "../components/FloatingActionButton";

const statusColor = (status) => {
  if (!status) return "#06b6d4";
  const s = status.toLowerCase();
  if (s.includes("confirm")) return "#06b6d4";
  if (s.includes("pending")) return "#f59e0b";
  if (s.includes("cancel")) return "#ef4444";
  if (s.includes("complete")) return "#10b981";
  return "#06b6d4";
};

const statusLabel = (status) => {
  if (!status) return "Confirmed";
  const s = status.toLowerCase();
  if (s.includes("confirm")) return "Confirmed";
  if (s.includes("pending")) return "Pending";
  if (s.includes("cancel")) return "Cancelled";
  if (s.includes("complete")) return "Completed";
  return status;
};

function formatTime(ts) {
  if (!ts) return "";
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  return isNaN(date)
    ? ""
    : date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getWeekDays(currentDate) {
  const curr = new Date(currentDate);
  const first = curr.getDate() - curr.getDay();
  const days = [];

  for (let i = 0; i < 7; i++) {
    const day = new Date(curr.setDate(first + i));
    days.push(new Date(day));
  }

  return days;
}

function generateTimeSlots() {
  const slots = [];
  // Generate slots from 12:00 AM (hour 0) to 11:00 PM (hour 23) - full 24 hours like Teams calendar
  for (let hour = 0; hour <= 23; hour++) {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);
    const timeStr = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    slots.push({ hour, display: timeStr });
  }
  return slots;
}

function AppointmentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useRef(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const { notificationCount } = useNotifications(); // Use context for real-time sync
  const [viewMode] = useState("week"); // week, day, list
  const [statusFilter, setStatusFilter] = useState("All");
  const [expandedSlots, setExpandedSlots] = useState(new Set()); // Track expanded hour slots: "dayIndex-hour"

  useEffect(() => {
    let unsubscribeAppointments = null;

    const setupListeners = async (user) => {
      try {
        if (!user) {
          setLoading(false);
          setAppointments([]);
          return;
        }

        setLoading(true);

        // Check and send any pending notifications from previous sessions
        await checkAndSendPendingNotifications(user.uid);

        // Real-time listener for appointments
        unsubscribeAppointments = onSnapshot(
          query(
            collection(db, "appointments"),
            where("userId", "==", user.uid),
          ),
          (snap) => {
            const data = snap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .filter((appt) => !appt.deleted); // Filter out soft-deleted appointments
            data.sort((a, b) => {
              const aTime = a.start?.toMillis() || 0;
              const bTime = b.start?.toMillis() || 0;
              return aTime - bTime;
            });
            setAppointments(data);

            // Schedule notifications for future appointments
            const now = new Date();
            for (const appointment of data) {
              const apptStart = appointment.start?.toDate
                ? appointment.start.toDate()
                : new Date(appointment.start);
              // Only schedule if appointment is in the future and not completed or cancelled
              if (
                apptStart > now &&
                appointment.status !== "Cancelled" &&
                appointment.status !== "Completed"
              ) {
                scheduleAppointmentStartNotification(
                  user.uid,
                  appointment,
                ).catch((e) =>
                  console.warn("Failed to schedule notification", e),
                );
              }
            }

            setLoading(false);
          },
          (error) => {
            console.warn("Failed to fetch appointments", error);
            setLoading(false);
          },
        );
      } catch (e) {
        console.warn("Failed to setup listeners", e);
        setLoading(false);
      }
    };

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setupListeners(user);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const currentUser = auth.currentUser;
        if (currentUser) {
          setupListeners(currentUser);
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      unsubscribeAuth();
      if (unsubscribeAppointments) unsubscribeAppointments();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const weekDays = useMemo(() => getWeekDays(currentWeek), [currentWeek]);
  const timeSlots = useMemo(() => generateTimeSlots(), []);

  // Always show all 24 hours (12:00 AM to 11:00 PM) - no empty rows after 11:00 PM
  const visibleTimeSlots = useMemo(() => {
    // Return all slots from 12:00 AM (hour 0) to 11:00 PM (hour 23)
    return timeSlots.filter((slot) => slot.hour >= 0 && slot.hour <= 23);
  }, [timeSlots]);

  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeek(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeek(newDate);
  };

  // Scroll to show today when coming from dashboard
  useEffect(() => {
    if (location.state?.focusToday && scrollContainerRef.current && !loading) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const todayIndex = new Date().getDay();
          const scrollPosition = Math.max(0, todayIndex * 128 - 100);
          scrollContainerRef.current.scrollLeft = scrollPosition;
        }
      }, 100);
    }
  }, [location.state?.focusToday, loading]);

  const isToday = (date) => {
    return isSameDay(date, new Date());
  };

  const monthYear = currentWeek.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  // Calculate status counts
  const statusCounts = useMemo(() => {
    const counts = {
      All: appointments.length,
      Confirmed: 0,
      Pending: 0,
      Completed: 0,
      Cancelled: 0,
    };
    appointments.forEach((appt) => {
      const status = appt.status || "Confirmed";
      const statusKey = statusLabel(status);
      if (counts.hasOwnProperty(statusKey)) {
        counts[statusKey]++;
      }
    });
    return counts;
  }, [appointments]);

  // Filter appointments by status
  const filteredAppointments = useMemo(() => {
    if (statusFilter === "All") return appointments;
    return appointments.filter((appt) => {
      const status = appt.status || "Confirmed";
      return statusLabel(status) === statusFilter;
    });
  }, [appointments, statusFilter]);

  const getAppointmentsForSlot = (day, timeSlot) => {
    const slotAppointments = filteredAppointments.filter((appt) => {
      if (!appt.start) return false;
      const apptStartDate = appt.start?.toDate
        ? appt.start.toDate()
        : new Date(appt.start);
      if (isNaN(apptStartDate)) return false;

      if (!isSameDay(apptStartDate, day)) return false;

      const apptStartHour = apptStartDate.getHours();
      const slotHour = timeSlot.hour;

      // Appointment should appear in the slot where it starts
      // This ensures appointments are placed in the correct row based on their start time
      return apptStartHour === slotHour;
    });

    // Sort appointments within the slot by their actual start time (including minutes)
    return slotAppointments.sort((a, b) => {
      const aDate = a.start?.toDate ? a.start.toDate() : new Date(a.start);
      const bDate = b.start?.toDate ? b.start.toDate() : new Date(b.start);
      return aDate.getTime() - bDate.getTime();
    });
  };

  const toggleSlotExpansion = (dayIdx, hour) => {
    const key = `${dayIdx}-${hour}`;
    setExpandedSlots((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        background: "#f8fafc",
      }}
    >
      {/* Header */}
      <header
        style={{
          background: "white",
          borderBottom: "1px solid #e2e8f0",
          flexShrink: 0,
        }}
      >
        {/* Month Navigation */}
        <div
          style={{
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <button
              onClick={goToPreviousWeek}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                color: "#64748b",
                fontSize: "20px",
              }}
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <h1
              style={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#1e293b",
                margin: 0,
              }}
            >
              {monthYear}
            </h1>
            <button
              onClick={goToNextWeek}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                color: "#64748b",
                fontSize: "20px",
              }}
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>

          <button
            onClick={() => navigate("/notifications")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              position: "relative",
              display: "flex",
              alignItems: "center",
              color: "#64748b",
            }}
          >
            <span className="material-symbols-outlined">notifications</span>
            {notificationCount > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "4px",
                  right: "4px",
                  background: "#ef4444",
                  color: "white",
                  borderRadius: "10px",
                  padding: "2px 6px",
                  fontSize: "10px",
                  fontWeight: 600,
                  minWidth: "16px",
                  textAlign: "center",
                }}
              >
                {notificationCount}
              </span>
            )}
          </button>
        </div>

        {/* Status Filters - Horizontal Scrollable */}
        <div
          style={{
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
            padding: "0 24px 16px",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              gap: "8px",
            }}
          >
            {["All", "Confirmed", "Pending", "Completed", "Cancelled"].map(
              (status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "20px",
                    border:
                      statusFilter === status ? "none" : "1px solid #e2e8f0",
                    background: statusFilter === status ? "#06b6d4" : "white",
                    color: statusFilter === status ? "white" : "#64748b",
                    fontSize: "14px",
                    fontWeight: statusFilter === status ? 600 : 500,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  {status}
                  {statusCounts[status] > 0 && (
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                      }}
                    >
                      {statusCounts[status]}
                    </span>
                  )}
                </button>
              ),
            )}
          </div>
        </div>
      </header>

      {/* Calendar Grid */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          background: "white",
          paddingBottom: "20px",
          position: "relative",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#94a3b8",
              fontSize: "14px",
            }}
          >
            Loading appointments...
          </div>
        ) : viewMode === "week" ? (
          <div
            style={{
              display: "flex",
              height: "fit-content",
              position: "relative",
              minWidth: "900px",
            }}
          >
            {/* Fixed Time Column */}
            <div
              style={{
                width: "60px",
                flexShrink: 0,
                background: "white",
                height: "fit-content",
                position: "sticky",
                left: 0,
                zIndex: 100,
                borderRight: "2px solid #e2e8f0",
                boxShadow: "2px 0 4px rgba(0, 0, 0, 0.05)",
              }}
            >
              {/* Time column header */}
              <div
                style={{
                  position: "sticky",
                  top: 0,
                  background: "white",
                  borderBottom: "1px solid #e2e8f0",
                  borderRight: "1px solid #e2e8f0",
                  padding: "16px 8px",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#94a3b8",
                  zIndex: 101,
                  height: "76px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                GMT+5:30
              </div>

              {/* Time slots */}
              {visibleTimeSlots.map((timeSlot, timeIdx) => {
                // Check if this hour is expanded in any day
                const isHourExpanded = weekDays.some((_, dayIdx) =>
                  expandedSlots.has(`${dayIdx}-${timeSlot.hour}`),
                );

                return (
                  <div key={`time-${timeIdx}`}>
                    {/* Main hour slot */}
                    <div
                      style={{
                        borderRight: "1px solid #e2e8f0",
                        borderBottom: "1px solid #f1f5f9",
                        padding: "8px",
                        fontSize: "11px",
                        color: "#94a3b8",
                        textAlign: "right",
                        minHeight: "80px",
                        height: isHourExpanded ? "auto" : "80px",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-end",
                        background: "white",
                      }}
                    >
                      {typeof timeSlot === "object"
                        ? timeSlot.display
                        : timeSlot}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Scrollable Days Grid */}
            <div
              ref={scrollContainerRef}
              style={{ flex: 1, height: "fit-content" }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(7, minmax(min(120px, 100%), 1fr))",
                  gridTemplateRows: `76px repeat(${visibleTimeSlots.length}, auto)`,
                  gridAutoRows: "auto",
                  minWidth: "min(840px, 100%)",
                  width: "100%",
                  height: "fit-content",
                }}
              >
                {/* Day headers */}
                {weekDays.map((day, idx) => (
                  <div
                    key={`header-${idx}`}
                    style={{
                      position: "sticky",
                      top: 0,
                      background: "white",
                      borderBottom: "1px solid #e2e8f0",
                      borderRight: idx < 6 ? "1px solid #e2e8f0" : "none",
                      padding: "12px 16px",
                      textAlign: "center",
                      zIndex: 20,
                      height: "76px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: isToday(day) ? "#06b6d4" : "#64748b",
                        marginBottom: "4px",
                      }}
                    >
                      {day.toLocaleDateString(undefined, { weekday: "short" })}
                    </div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: 600,
                        color: isToday(day) ? "#06b6d4" : "#1e293b",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        background: isToday(day) ? "#e0f2fe" : "transparent",
                      }}
                    >
                      {day.getDate()}
                    </div>
                  </div>
                ))}

                {/* Time slots and day cells */}
                {visibleTimeSlots.map((timeSlot, timeIdx) =>
                  weekDays.map((day, dayIdx) => {
                    const slotAppointments = getAppointmentsForSlot(
                      day,
                      timeSlot,
                    );
                    const slotKey = `${dayIdx}-${timeSlot.hour}`;
                    const isExpanded = expandedSlots.has(slotKey);
                    const MAX_VISIBLE = 2; // Show first 2 appointments, then "Load more"
                    const hasMore = slotAppointments.length > MAX_VISIBLE;
                    const visibleAppointments = isExpanded
                      ? slotAppointments
                      : slotAppointments.slice(0, MAX_VISIBLE);

                    return (
                      <div key={`cell-${timeIdx}-${dayIdx}`}>
                        {/* Main hour slot */}
                        <div
                          style={{
                            borderRight:
                              dayIdx < 6 ? "1px solid #e2e8f0" : "none",
                            borderBottom: "1px solid #f1f5f9",
                            padding: "4px",
                            minHeight: "80px",
                            height: isExpanded
                              ? "auto"
                              : hasMore
                                ? "auto"
                                : "80px",
                            background: isToday(day) ? "#fafbfc" : "white",
                            position: "relative",
                            cursor: "pointer",
                            overflow: "visible",
                          }}
                          onClick={() => navigate("/appointments/new")}
                        >
                          {visibleAppointments.map((appt) => {
                            const color = statusColor(appt.status);
                            return (
                              <div
                                key={appt.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/appointments/${appt.id}`);
                                }}
                                style={{
                                  background: color,
                                  borderRadius: "4px",
                                  padding:
                                    "clamp(4px, 1vw, 6px) clamp(6px, 1.5vw, 8px)",
                                  marginBottom: "4px",
                                  fontSize: "clamp(10px, 2vw, 12px)",
                                  color: "white",
                                  fontWeight: 500,
                                  cursor: "pointer",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                  transition: "all 0.2s",
                                  minWidth: "0",
                                  width: "100%",
                                }}
                                title={`${appt.patientName} - ${appt.reason}`}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "clamp(2px, 0.5vw, 4px)",
                                    marginBottom: "2px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "clamp(8px, 1.5vw, 10px)",
                                      opacity: 0.9,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {(() => {
                                      const startTime = formatTime(appt.start);
                                      let endTime = null;
                                      if (appt.end) {
                                        endTime = formatTime(appt.end);
                                      } else if (appt.start) {
                                        const startDate = appt.start?.toDate
                                          ? appt.start.toDate()
                                          : new Date(appt.start);
                                        const defaultEnd = new Date(
                                          startDate.getTime() + 30 * 60000,
                                        );
                                        endTime = formatTime(defaultEnd);
                                      }
                                      return endTime
                                        ? `${startTime} - ${endTime}`
                                        : startTime;
                                    })()}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: "clamp(9px, 1.8vw, 11px)",
                                    marginBottom: "2px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {appt.patientName}
                                </div>
                                <div
                                  style={{
                                    fontSize: "clamp(8px, 1.5vw, 10px)",
                                    opacity: 0.9,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {appt.reason}
                                </div>
                              </div>
                            );
                          })}

                          {/* Load more button */}
                          {hasMore && !isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSlotExpansion(dayIdx, timeSlot.hour);
                              }}
                              style={{
                                width: "calc(100% - 8px)",
                                padding: "clamp(4px, 1vw, 6px)",
                                marginTop: "4px",
                                background: "#f8fafc",
                                border: "1px dashed #cbd5e1",
                                borderRadius: "4px",
                                color: "#64748b",
                                fontSize: "clamp(9px, 1.8vw, 11px)",
                                fontWeight: 600,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "clamp(2px, 0.5vw, 4px)",
                                position: "relative",
                                zIndex: 10,
                                transition: "all 0.2s",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#f1f5f9";
                                e.currentTarget.style.borderColor = "#94a3b8";
                                e.currentTarget.style.color = "#475569";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#f8fafc";
                                e.currentTarget.style.borderColor = "#cbd5e1";
                                e.currentTarget.style.color = "#64748b";
                              }}
                            >
                              <span
                                className="material-symbols-outlined"
                                style={{ fontSize: "clamp(12px, 2.5vw, 16px)" }}
                              >
                                expand_more
                              </span>
                              Load more ({slotAppointments.length - MAX_VISIBLE}{" "}
                              more)
                            </button>
                          )}

                          {/* Collapse button when expanded */}
                          {isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSlotExpansion(dayIdx, timeSlot.hour);
                              }}
                              style={{
                                width: "100%",
                                padding: "clamp(4px, 1vw, 6px)",
                                marginTop: "4px",
                                background: "transparent",
                                border: "1px dashed #cbd5e1",
                                borderRadius: "4px",
                                color: "#64748b",
                                fontSize: "clamp(9px, 1.8vw, 11px)",
                                fontWeight: 500,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "clamp(2px, 0.5vw, 4px)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <span
                                className="material-symbols-outlined"
                                style={{ fontSize: "clamp(12px, 2.5vw, 16px)" }}
                              >
                                expand_less
                              </span>
                              Show less
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  }),
                )}
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: "24px",
              textAlign: "center",
              color: "#94a3b8",
            }}
          >
            {viewMode === "day"
              ? "Day view - Coming soon"
              : "List view - Use the original list view"}
          </div>
        )}
      </div>

      <FloatingActionButton
        onClick={() => navigate("/appointments/new")}
        ariaLabel="Add Appointment"
      />

      {/* Bottom Nav */}
      <nav className="appts-bottom-nav">
        <button className="appts-nav-item" onClick={() => navigate("/doctor")}>
          <span className="material-symbols-outlined">home</span>
          <span>Home</span>
        </button>
        <button className="appts-nav-item active">
          <span className="material-symbols-outlined fill-current">
            calendar_month
          </span>
          <span>Appointments</span>
        </button>
        <button
          className="appts-nav-item"
          onClick={() => navigate("/patients")}
        >
          <span className="material-symbols-outlined">groups</span>
          <span>Patients</span>
        </button>
        <button className="appts-nav-item" onClick={() => navigate("/reports")}>
          <span className="material-symbols-outlined">analytics</span>
          <span>Reports</span>
        </button>
      </nav>
    </div>
  );
}

export default AppointmentsPage;
